import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Button, Spinner } from "reactstrap";
import { downloadScheduleExportFile } from "api/report-schedules/api";
import { getScheduleTimezoneOffsetHours, toLocalDayKey } from "./reportScheduleMapper";
import {
  buildMonthCalendarDays,
  buildUpcomingRunsFromEntries,
  getDayStatusSummary,
  getUniqueReportLabels,
  groupRunsByReport,
  groupRunsByDayKey,
  groupUpcomingByDayKey,
  isPastOrToday,
  normalizeScheduleRuns,
} from "./reportScheduleCalendarUtils";

const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

const DEFAULT_COLORS = {
  panelBg: "transparent",
  fieldBg: "rgba(0,0,0,0.03)",
  fieldBorder: "#e9ecef",
  titleColor: "inherit",
  bodyColor: "inherit",
  mutedColor: "#6c757d",
  rowBorder: "#ececec",
};

const statusColor = (status) => {
  if (status === "success") return "#00bf9a";
  if (status === "failed") return "#fd5d93";
  if (status === "scheduled") return "#1d8cf8";
  return "#9a9a9a";
};

const ScheduleExportsCalendar = ({
  runs = [],
  upcomingRuns: upcomingRunsProp = null,
  isLoading = false,
  timezone = "GMT-8",
  colors: colorsProp,
  activeColorStyle = {},
  showMeta = false,
  onReportClick,
  className = "",
  fillHeight = false,
  title,
  titleIcon = null,
  headerExtra = null,
  visibleMonth: controlledMonth,
  onVisibleMonthChange,
}) => {
  const { t } = useTranslation();
  const colors = { ...DEFAULT_COLORS, ...colorsProp };
  const [internalMonth, setInternalMonth] = useState(() => moment().startOf("month"));
  const visibleMonth = controlledMonth || internalMonth;
  const [selectedDayKey, setSelectedDayKey] = useState(null);
  const [downloadingRunKey, setDownloadingRunKey] = useState(null);
  const [downloadError, setDownloadError] = useState("");
  const detailScrollRef = useRef(null);

  const year = visibleMonth.year();
  const month = visibleMonth.month() + 1;
  const offsetHours = useMemo(() => getScheduleTimezoneOffsetHours(timezone), [timezone]);

  const monthRuns = useMemo(() => {
    const monthKey = `${year}-${String(month).padStart(2, "0")}`;
    return normalizeScheduleRuns(runs).filter((run) => {
      const dayKey = toLocalDayKey(run.ran_at, offsetHours);
      return dayKey.startsWith(monthKey);
    });
  }, [runs, year, month, offsetHours]);

  const runsByDay = useMemo(
    () => groupRunsByDayKey(monthRuns, timezone),
    [monthRuns, timezone]
  );

  const monthUpcomingRuns = useMemo(() => {
    if (upcomingRunsProp) return upcomingRunsProp;
    return buildUpcomingRunsFromEntries(runs, timezone, year, month);
  }, [upcomingRunsProp, runs, timezone, year, month]);

  const upcomingByDay = useMemo(
    () => groupUpcomingByDayKey(monthUpcomingRuns, timezone),
    [monthUpcomingRuns, timezone]
  );

  const calendarDays = useMemo(
    () => buildMonthCalendarDays(year, month),
    [year, month]
  );

  useEffect(() => {
    setSelectedDayKey(null);
    setDownloadError("");
  }, [year, month]);

  const selectedDayPastRuns = useMemo(() => {
    if (!selectedDayKey) return [];
    if (!isPastOrToday(selectedDayKey, timezone)) return [];
    return runsByDay[selectedDayKey] || [];
  }, [selectedDayKey, runsByDay, timezone]);

  const selectedDayUpcomingRuns = useMemo(() => {
    if (!selectedDayKey) return [];
    return upcomingByDay[selectedDayKey] || [];
  }, [selectedDayKey, upcomingByDay]);

  const selectedRuns = useMemo(
    () => [...selectedDayPastRuns, ...selectedDayUpcomingRuns],
    [selectedDayPastRuns, selectedDayUpcomingRuns]
  );

  const selectedDayReports = useMemo(
    () => getUniqueReportLabels(selectedRuns),
    [selectedRuns]
  );
  const selectedDayGroups = useMemo(
    () => groupRunsByReport(selectedRuns),
    [selectedRuns]
  );

  const isSelectedDayFuture = selectedDayKey
    ? !isPastOrToday(selectedDayKey, timezone)
    : false;

  const setVisibleMonth = (updater) => {
    const next = typeof updater === "function" ? updater(visibleMonth) : updater;
    if (onVisibleMonthChange) {
      onVisibleMonthChange(next);
    } else {
      setInternalMonth(next);
    }
  };

  const handlePrevMonth = () => {
    setVisibleMonth((prev) => prev.clone().subtract(1, "month"));
  };

  const handleNextMonth = () => {
    const next = visibleMonth.clone().add(1, "month");
    if (next.isAfter(moment().add(12, "months"), "month")) return;
    setVisibleMonth(next);
  };

  const handleDayClick = (day) => {
    if (!day.inCurrentMonth) return;
    const dayRuns = runsByDay[day.dayKey] || [];
    const dayUpcoming = upcomingByDay[day.dayKey] || [];
    const isPast = isPastOrToday(day.dayKey, timezone);
    const hasPastRuns = isPast && dayRuns.length > 0;
    const hasUpcoming = dayUpcoming.length > 0;
    if (!hasPastRuns && !hasUpcoming) return;
    setSelectedDayKey(day.dayKey);
    setDownloadError("");
    requestAnimationFrame(() => {
      detailScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const handleCloseDetail = () => {
    setSelectedDayKey(null);
    setDownloadError("");
  };

  const renderMonthNav = () => (
    <>
      <Button color="secondary" outline size="sm" onClick={handlePrevMonth}>
        <i className="tim-icons icon-minimal-left" />
      </Button>
      <span
        style={{
          minWidth: 120,
          textAlign: "center",
          fontWeight: 600,
          fontSize: 14,
          color: colors.bodyColor,
        }}
      >
        {visibleMonth.format("MMMM YYYY")}
      </span>
      <Button color="secondary" outline size="sm" onClick={handleNextMonth} disabled={!canGoNextMonth}>
        <i className="tim-icons icon-minimal-right" />
      </Button>
    </>
  );

  const renderCloseButton = () => (
    <Button
      color="secondary"
      outline
      size="sm"
      onClick={handleCloseDetail}
      aria-label={t("common.close")}
      title={t("common.close")}
    >
      <i className="tim-icons icon-simple-remove" />
    </Button>
  );

  const renderHeaderActions = () => (
    <div className="d-flex align-items-center flex-shrink-0 flex-wrap justify-content-end" style={{ gap: 8 }}>
      {headerExtra}
      {selectedDayKey ? renderCloseButton() : renderMonthNav()}
    </div>
  );

  const handleDownloadRun = async (run, runKey) => {
    if (!run?.upload_file_id) return;
    setDownloadingRunKey(runKey);
    setDownloadError("");
    try {
      await downloadScheduleExportFile(
        run.upload_file_id,
        run.file_name || run.schedule_name || "schedule-export"
      );
    } catch (error) {
      setDownloadError(error?.message || t("reports.schedule.downloadCsvFailed"));
    } finally {
      setDownloadingRunKey(null);
    }
  };

  const getStatusLabel = (status) => {
    if (status === "success") return t("dashboard.statusSuccess");
    if (status === "failed") return t("dashboard.statusFailed");
    if (status === "scheduled") return t("dashboard.statusScheduled");
    return t("dashboard.statusSkipped");
  };

  const canGoNextMonth = visibleMonth
    .clone()
    .add(1, "month")
    .isSameOrBefore(moment().add(12, "months"), "month");
  const accentColor = activeColorStyle.borderColor || "#1d8cf8";
  const isDetailView = Boolean(selectedDayKey);

  const renderRunItems = (runs = selectedRuns) =>
    runs.map((run, index) => {
      const runKey = `${run.isUpcoming ? "upcoming" : "past"}-${run.ran_at}-${run.schedule_id || ""}-${index}`;
      const isDownloading = downloadingRunKey === runKey;
      const metaRun = run;
      const isUpcoming = !!run.isUpcoming;

      return (
        <div
          key={runKey}
          style={{
            borderTop: index === 0 ? "none" : `1px solid ${colors.rowBorder}`,
            paddingTop: index === 0 ? 0 : 12,
            marginTop: index === 0 ? 0 : 12,
          }}
        >
          <div className="d-flex flex-wrap justify-content-between align-items-start" style={{ gap: 8 }}>
            <div>
              <div style={{ fontWeight: 600, color: colors.bodyColor }}>
                {moment(run.ran_at).format("HH:mm")}
                <span
                  className="ml-2"
                  style={{ color: statusColor(run.status), fontSize: 12, textTransform: "uppercase" }}
                >
                  {getStatusLabel(run.status)}
                </span>
              </div>
              {showMeta && metaRun.schedule_name && (
                <div className="text-muted small mt-1">
                  {t("dashboard.scheduleName")}: {metaRun.schedule_name}
                </div>
              )}
              {!isUpcoming && (
                <div className="text-muted small mt-1">
                  {run.row_count != null && (
                    <span className="mr-3">
                      {t("reports.schedule.runHistoryRows")}: {run.row_count}
                    </span>
                  )}
                  {run.trigger && (
                    <span>
                      {t("reports.schedule.runHistoryTrigger")}: {run.trigger}
                    </span>
                  )}
                </div>
              )}
              {!isUpcoming && run.file_name && (
                <div className="text-muted small mt-1">
                  {t("reports.schedule.runHistoryFile")}: {run.file_name}
                </div>
              )}
              {isUpcoming && (
                <div className="text-muted small mt-1">
                  {t("reports.schedule.nextRunAt")}: {moment(run.ran_at).format("MMM D, YYYY HH:mm")}
                </div>
              )}
            </div>

            {isUpcoming ? (
              <span className="text-muted small">{t("reports.schedule.calendarNotYetCreated")}</span>
            ) : run.upload_file_id ? (
              <Button
                size="sm"
                style={activeColorStyle}
                disabled={isDownloading}
                onClick={() => handleDownloadRun(run, runKey)}
              >
                {isDownloading ? (
                  <>
                    <Spinner size="sm" className="mr-1" />
                    {t("reports.schedule.downloadingCsv")}
                  </>
                ) : (
                  <>
                    <i className="tim-icons icon-cloud-download-93 mr-1" />
                    {t("dashboard.downloadFile")}
                  </>
                )}
              </Button>
            ) : (
              <span className="text-muted small">{t("reports.schedule.noFileAvailable")}</span>
            )}
          </div>

          {!isUpcoming && (run.message || run.status === "failed") && (
            <div
              className="schedule-exports-calendar-log py-2 px-3 mt-2 mb-0"
              style={{
                fontSize: 13,
                border: `1px solid ${run.status === "failed" ? "#fd5d93" : colors.fieldBorder}`,
                backgroundColor:
                  run.status === "failed"
                    ? colors.logDangerBg || "rgba(253, 93, 147, 0.12)"
                    : colors.logInfoBg || colors.fieldBg,
                color: colors.logTextColor || colors.bodyColor,
              }}
            >
              <strong>{t("reports.schedule.runLog")}:</strong>{" "}
              {run.message || t("reports.schedule.calendarNoLogMessage")}
            </div>
          )}
        </div>
      );
    });

  const renderDetailHeader = () => (
    <div className="mb-3">
      <h6 className="mb-2" style={{ color: colors.titleColor }}>
        {moment(selectedDayKey, "YYYY-MM-DD").format("MMMM D, YYYY")}
      </h6>
      {selectedDayReports.length > 0 && (
        <div className="schedule-exports-calendar-day-report-list">
          <span className="schedule-exports-calendar-day-report-label" style={{ color: colors.mutedColor }}>
            {t("dashboard.reportName")}:
          </span>
          {selectedDayReports.map((report, idx) => (
            <span key={report.key}>
              {idx > 0 && <span style={{ color: colors.mutedColor }}> · </span>}
              {report.reportId && onReportClick ? (
                <span
                  className="log-action-link"
                  style={{ fontWeight: 600 }}
                  onClick={() => onReportClick(report.reportId)}
                >
                  {report.label}
                </span>
              ) : (
                <span style={{ color: colors.bodyColor, fontWeight: 600 }}>{report.label}</span>
              )}
            </span>
          ))}
        </div>
      )}
      {isSelectedDayFuture && (
        <div className="text-muted small mt-2">{t("reports.schedule.calendarUpcomingDetail")}</div>
      )}
    </div>
  );

  const sortRunsForDisplay = (runs = []) => {
    const past = runs.filter((run) => !run.isUpcoming).sort((a, b) => new Date(b.ran_at) - new Date(a.ran_at));
    const upcoming = runs.filter((run) => run.isUpcoming).sort((a, b) => new Date(a.ran_at) - new Date(b.ran_at));
    return [...past, ...upcoming];
  };

  const renderGroupedRunItems = () =>
    selectedDayGroups.map((group, groupIndex) => (
      <div key={group.key} style={{ marginTop: groupIndex === 0 ? 0 : 16 }}>
        {selectedDayGroups.length > 1 && (
          <div
            className="mb-2 pb-2"
            style={{ borderBottom: `1px solid ${colors.rowBorder}` }}
          >
            <div className="schedule-exports-calendar-report-group-title">
              {group.reportId && onReportClick ? (
                <span
                  className="log-action-link"
                  onClick={() => onReportClick(group.reportId)}
                >
                  {group.reportLabel}
                </span>
              ) : (
                group.reportLabel
              )}
            </div>
            {group.scheduleName && (
              <div className="text-muted small mt-1">
                {t("dashboard.scheduleName")}: {group.scheduleName}
              </div>
            )}
          </div>
        )}
        {renderRunItems(sortRunsForDisplay(group.runs))}
      </div>
    ));

  const getDayReportHint = (dayRuns) => {
    const labels = getUniqueReportLabels(dayRuns);
    if (labels.length === 0) return "";
    if (labels.length === 1) {
      const label = labels[0].label;
      return label.length > 14 ? `${label.slice(0, 14)}…` : label;
    }
    return t("reports.schedule.calendarReportCount", { count: labels.length });
  };

  return (
    <div
      className={`schedule-exports-calendar ${fillHeight ? "schedule-exports-calendar--fill" : ""} ${
        isDetailView && fillHeight ? "schedule-exports-calendar--detail" : ""
      } ${className}`.trim()}
    >
      <style>{`
        .schedule-exports-calendar.schedule-exports-calendar--fill {
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }
        .schedule-exports-calendar.schedule-exports-calendar--fill .schedule-exports-calendar-grid {
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
        }
        .schedule-exports-calendar.schedule-exports-calendar--fill .schedule-exports-calendar-days {
          flex: 1;
          grid-auto-rows: 1fr;
        }
        .schedule-exports-calendar.schedule-exports-calendar--fill .schedule-exports-calendar-day {
          min-height: 0;
          height: 100%;
        }
        .schedule-exports-calendar .schedule-exports-calendar-header {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 8px;
          gap: 12px;
          flex-wrap: nowrap;
        }
        .schedule-exports-calendar .schedule-exports-calendar-header .card-category {
          text-transform: none !important;
          font-weight: 600 !important;
          font-size: 13px !important;
        }
        .schedule-exports-calendar.schedule-exports-calendar--fill.schedule-exports-calendar--detail
          .schedule-exports-calendar-detail-scroll {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          overflow-x: hidden;
          border: 1px solid ${colors.fieldBorder};
          background-color: ${colors.fieldBg};
          padding: 12px 14px;
        }
        .schedule-exports-calendar .schedule-exports-calendar-grid {
          overflow: hidden;
          border: 1px solid ${colors.fieldBorder};
          background-color: ${colors.panelBg};
        }
        .schedule-exports-calendar .schedule-exports-calendar-weekdays,
        .schedule-exports-calendar .schedule-exports-calendar-days {
          display: grid;
          grid-template-columns: repeat(7, minmax(0, 1fr));
        }
        .schedule-exports-calendar .schedule-exports-calendar-weekday {
          padding: 8px 4px;
          text-align: center;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          color: ${colors.mutedColor};
          border-bottom: 1px solid ${colors.rowBorder};
        }
        .schedule-exports-calendar .schedule-exports-calendar-day {
          min-height: 64px;
          padding: 6px;
          border: none;
          text-align: left;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          align-items: flex-start;
          border-right: 1px solid ${colors.rowBorder};
          border-bottom: 1px solid ${colors.rowBorder};
        }
        .schedule-exports-calendar .schedule-exports-calendar-day:disabled {
          opacity: 0.55;
        }
        .schedule-exports-calendar .schedule-exports-calendar-day-number {
          font-size: 13px;
          font-weight: 600;
          line-height: 1.2;
        }
        .schedule-exports-calendar .schedule-exports-calendar-day-markers {
          display: flex;
          align-items: center;
          gap: 4px;
          min-height: 10px;
        }
        .schedule-exports-calendar .schedule-exports-calendar-marker {
          width: 8px;
          height: 8px;
          display: inline-block;
        }
        .schedule-exports-calendar .schedule-exports-calendar-marker--scheduled {
          background-color: transparent !important;
          border: 2px solid #1d8cf8;
          box-sizing: border-box;
        }
        .schedule-exports-calendar .schedule-exports-calendar-day-empty {
          font-size: 10px;
          line-height: 1.2;
          color: ${colors.mutedColor};
          margin-top: auto;
        }
        .schedule-exports-calendar .schedule-exports-calendar-day-report-hint {
          font-size: 9px;
          line-height: 1.2;
          color: ${colors.bodyColor};
          font-weight: 600;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .schedule-exports-calendar .schedule-exports-calendar-day-report-list {
          font-size: 12px;
          line-height: 1.5;
        }
        .schedule-exports-calendar .schedule-exports-calendar-day-report-label {
          font-weight: 600;
          margin-right: 6px;
        }
        .schedule-exports-calendar .schedule-exports-calendar-report-group-title {
          font-size: 13px;
          font-weight: 700;
          color: ${colors.bodyColor};
        }
      `}</style>

      {(title || titleIcon) && (
        <div className="schedule-exports-calendar-header dashboard-panel-header">
          <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
            {titleIcon}
            {title && (
              <h5 className="card-category mb-0">
                {title}
              </h5>
            )}
          </div>
          {renderHeaderActions()}
        </div>
      )}

      {!title && !titleIcon && (
        <div className="d-flex align-items-center justify-content-end mb-2">
          {renderHeaderActions()}
        </div>
      )}

      {isLoading && !isDetailView && (
        <div className="text-muted small mb-2 d-flex align-items-center">
          <Spinner size="sm" className="mr-2" />
          {t("reports.schedule.calendarLoading")}
        </div>
      )}

      {!isDetailView && (
      <div className="schedule-exports-calendar-grid">
        <div className="schedule-exports-calendar-weekdays">
          {WEEKDAY_KEYS.map((key) => (
            <div key={key} className="schedule-exports-calendar-weekday">
              {t(`reports.schedule.weekdays.${key}`)}
            </div>
          ))}
        </div>

        <div className="schedule-exports-calendar-days">
          {calendarDays.map((day) => {
            const dayRuns = runsByDay[day.dayKey] || [];
            const dayUpcoming = upcomingByDay[day.dayKey] || [];
            const hasRuns = dayRuns.length > 0;
            const hasUpcoming = dayUpcoming.length > 0;
            const isPast = isPastOrToday(day.dayKey, timezone);
            const isSelectable = day.inCurrentMonth && ((isPast && hasRuns) || hasUpcoming);
            const isSelected = selectedDayKey === day.dayKey;
            const isScheduled = hasUpcoming;
            const summaryStatus = getDayStatusSummary(dayRuns);
            const dayReportHint =
              hasRuns && isPast
                ? getDayReportHint(dayRuns)
                : hasUpcoming
                  ? getDayReportHint(dayUpcoming)
                  : "";
            const dayReportTooltip = [
              ...getUniqueReportLabels(dayRuns),
              ...getUniqueReportLabels(dayUpcoming),
            ]
              .map((r) => r.label)
              .join(", ");

            return (
              <button
                key={day.dayKey}
                type="button"
                className="schedule-exports-calendar-day"
                onClick={() => handleDayClick(day)}
                disabled={!isSelectable}
                style={{
                  backgroundColor: isSelected ? colors.fieldBg : colors.panelBg,
                  color: day.inCurrentMonth ? colors.bodyColor : colors.mutedColor,
                  opacity: day.inCurrentMonth ? 1 : 0.45,
                  cursor: isSelectable ? "pointer" : "default",
                  outline: isSelected ? `2px solid ${accentColor}` : "none",
                }}
              >
                <span className="schedule-exports-calendar-day-number">{day.date.date()}</span>
                <div className="schedule-exports-calendar-day-markers">
                  {hasRuns && isPast && (
                    <span
                      className="schedule-exports-calendar-marker"
                      style={{ backgroundColor: statusColor(summaryStatus) }}
                      title={dayReportTooltip}
                    />
                  )}
                  {dayReportHint && (
                    <span
                      className="schedule-exports-calendar-day-report-hint"
                      title={dayReportTooltip}
                    >
                      {dayReportHint}
                    </span>
                  )}
                  {isScheduled && (
                    <span
                      className="schedule-exports-calendar-marker schedule-exports-calendar-marker--scheduled"
                      title={t("reports.schedule.calendarScheduled")}
                    />
                  )}
                  {day.inCurrentMonth && isPast && !hasRuns && !isScheduled && (
                    <span className="schedule-exports-calendar-day-empty">
                      {t("reports.schedule.calendarDayNoData")}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      )}

      {isDetailView && fillHeight && (
        <div ref={detailScrollRef} className="schedule-exports-calendar-detail-scroll">
          {renderDetailHeader()}
          {renderGroupedRunItems()}
          {downloadError && (
            <div
              className="schedule-exports-calendar-log py-2 px-3 mt-2 mb-0"
              style={{
                fontSize: 13,
                border: "1px solid #fd5d93",
                backgroundColor: colors.logDangerBg || "rgba(253, 93, 147, 0.12)",
                color: colors.logTextColor || colors.bodyColor,
              }}
            >
              {downloadError}
            </div>
          )}
        </div>
      )}

      {isDetailView && !fillHeight && (
        <div
          className="schedule-exports-calendar-detail mt-3"
          style={{
            border: `1px solid ${colors.fieldBorder}`,
            backgroundColor: colors.fieldBg,
            padding: "12px 14px",
          }}
        >
          {renderDetailHeader()}
          {renderGroupedRunItems()}
          {downloadError && (
            <div
              className="schedule-exports-calendar-log py-2 px-3 mt-2 mb-0"
              style={{
                fontSize: 13,
                border: "1px solid #fd5d93",
                backgroundColor: colors.logDangerBg || "rgba(253, 93, 147, 0.12)",
                color: colors.logTextColor || colors.bodyColor,
              }}
            >
              {downloadError}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScheduleExportsCalendar;
